import { apiConstants } from "@data/constants/apiConstants"
import { authConstants } from "@data/constants/authConstants"
import { formDataConstants } from "@data/constants/formDataConstants"
import { attendanceFlagsService } from "@data/services/attendanceFlagsService"
import { authService } from "@data/services/authService"
import { groupAttributionProposalsService } from "@data/services/groupAttributionProposalsService"
import { liveService } from "@data/services/liveService"
import { studentsService } from "@data/services/studentsService"
import { subscriptionService } from "@data/services/subscriptionService"
import { substitutionProposalsService } from "@data/services/substitutionProposalsService"

/**
 * Fusion clé par clé du user de `formData`, sur le modèle de `currentUser`.
 *
 * `formData.user` est alimenté par `getSubscribePricing`, qui renvoie un
 * `UserSubscriptionResource` : il porte des clés que les autres endpoints
 * d'abonnement ne renvoient PAS (`upcoming_invoice_data`, `planned_subscription`,
 * `current_study_programs`, `stripe_balance`, …). Écraser l'objet entier avec un
 * `UserResource` fait donc disparaître le prix affiché et la prochaine échéance
 * du portail jusqu'au prochain `getSubscribePricing`.
 */
export const mergeFormDataUser = (previous, payload) => {
    if (!payload) return previous

    return { ...(previous ?? {}), ...payload }
}

const initialState = {
    activity: null,
    answerInput: null,
    holidayOptions: [],
    trialOptions: [],
    reportFile: null,
    uploadedFiles: {},
    substitutionProposalByHash: null,
    groupAttributionProposalByHash: null,
}

export default function formData(state = initialState, action) {
    switch (action.type) {
        case liveService.getAllMyNextLiveSessionsAsParent.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                user: {
                    ...state.user,
                    live_sessions: state.user?.live_sessions?.map((session) => {
                        const newSession = action.data?.live_sessions?.find(
                            (newSession) => newSession.id === session.id
                        )
                        return newSession ? newSession : session
                    }),
                },
            }
        case liveService.reportAbsenceAsParent.name + apiConstants._SUCCESS:
            return {
                ...state,
                user: {
                    ...state.user,
                    live_sessions: state.user.live_sessions.map((session) => ({
                        ...session,
                        attendance_flags:
                            action.data?.id === session?.id
                                ? action.data?.attendance_flags
                                : session?.attendance_flags,
                    })),
                },
            }
        case attendanceFlagsService.cancelAttendanceFlag.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                user: {
                    ...state.user,
                    live_sessions: state.user?.live_sessions?.map(
                        (session) => ({
                            ...session,
                            attendance_flags: session.attendance_flags?.filter(
                                (flag) => flag.id !== action?.routeParams?.id
                            ),
                        })
                    ),
                },
            }
        case formDataConstants.SET_ANSWER_INPUT:
            return {
                ...state,
                answerInput: action.data,
            }
        case formDataConstants.SAVE_ACTIVITY:
            return {
                ...state,
                activity: action.data,
            }

        case formDataConstants.SAVE_REPORT_FILE:
            return {
                ...state,
                reportFile: action.data,
            }

        case formDataConstants.CLEAR_REPORT_FILE:
            return {
                ...state,
                reportFile: null,
            }

        case formDataConstants.SAVE_UPLOADED_FILES:
            return {
                ...state,
                uploadedFiles: {
                    ...state.uploadedFiles,
                    [action.formId]: action.data,
                },
            }

        case formDataConstants.RESET_UPLOADED_FILES:
            return {
                ...state,
                uploadedFiles: {
                    ...state.uploadedFiles,
                    [action.formId]: undefined,
                },
            }

        case substitutionProposalsService.getSubstitutionProposalByHash.name +
            apiConstants._SUCCESS:
        case substitutionProposalsService.updateSubstitutionProposalStatusByHash
            .name + apiConstants._SUCCESS:
            return {
                ...state,
                substitutionProposalByHash: action.data,
            }

        case groupAttributionProposalsService.getGroupAttributionProposalByHash
            .name + apiConstants._SUCCESS:
        case groupAttributionProposalsService
            .updateGroupAttributionProposalStatusByHash.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                groupAttributionProposalByHash: action.data,
            }

        // Pause balance reducer — stores remaining quota for the parent
        // pause modal so the CTA can be gated and remaining days surfaced.
        // Cleared on pause / cancel so the next open re-fetches the latest
        // state from GET /api/v1/subscription/pause/balance.
        case subscriptionService.getParentPauseBalance.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                pauseBalance: action.data,
            }
        case subscriptionService.pauseSubscriptionParent.name +
            apiConstants._SUCCESS:
        case subscriptionService.cancelParentPause.name + apiConstants._SUCCESS:
            return {
                ...state,
                user: action.data,
                pauseBalance: null,
            }

        case authConstants.LOGOUT_401:
        case authService.logout.name + apiConstants._SUCCESS:
        case authService.revokeSanctumToken.name + apiConstants._SUCCESS:
        case authService.deleteAccount.name + apiConstants._SUCCESS:
        case formDataConstants.CLEAR_ACTIVITY:
            return {
                ...state,
                activity: null,
            }

        case authService.bootstrapImpersonation.name + apiConstants._SUCCESS:
        case authService.exitImpersonation.name + apiConstants._SUCCESS:
            // formData.user is the data source for the parent home screen
            // (live_sessions, study programs, payment_blocked, …) and is
            // populated by getSubscribePricing. Without a reset here, a new
            // impersonation tab inherits the previous target's user from
            // redux-persist and renders their name in session cards / lists
            // until the next getSubscribePricing for the new identity lands.
            // Reset the user-bound slice; static catalog data (pricingTable,
            // schoolSubjects from staticData) is identity-agnostic and stays.
            return {
                ...state,
                user: undefined,
                billingPortalUrl: undefined,
                pauseBalance: null,
            }
        case subscriptionService.getPauseResumeChoice.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                schoolSubjects: action.all_data?.school_subjects,
                nextPaymentDate: action.all_data?.next_payment_date,
                displayName: action.all_data?.display_name,
                remainingDiscounts: action.all_data?.remaining_discounts,
                user: action.all_data?.user, // id, roles, subscription_state, trial_ends_at
            }
        case subscriptionService.getParentPauseBalance.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                pauseBalance: action.data,
            }
        case subscriptionService.pauseSubscriptionParent.name +
            apiConstants._SUCCESS:
        case subscriptionService.cancelParentPause.name + apiConstants._SUCCESS:
            return {
                ...state,
                user: action.data,
                pauseBalance: null,
            }
        // Activation self-service de la suite (produit d'appel → régulier) — le
        // back renvoie le UserResource refreshed (avec `upcoming_activation`
        // mis à jour : `planned` renseigné). Sans ce case, le portail
        // resterait sur l'ancien snapshot.
        case subscriptionService.activateSubscriptionSuite.name +
            apiConstants._SUCCESS:
        // Prolongation des cours d'été : la borne de facturation bouge, donc
        // `upcoming_activation.due_date` et le bloc "trou de couverture" doivent
        // se recalculer sur le UserResource renvoyé.
        case subscriptionService.extendSummerSubscription.name +
            apiConstants._SUCCESS:
            // Fusion clé par clé : ces trois endpoints renvoient un
            // `UserResource`, plus étroit que le `UserSubscriptionResource` déjà
            // en place. Un remplacement effacerait le prix et la prochaine
            // échéance affichés juste à côté du bloc qu'on vient de mettre à jour.
            return {
                ...state,
                user: mergeFormDataUser(state.user, action.data),
            }
        // Le portail Abonnement lit `formData.user` avant `currentUser` : sans ce case, il
        // garderait l'ancien jour. On n'écrit que sur l'élève qu'il tient (une réponse revenue
        // après un changement d'enfant vise l'enfant d'avant).
        case studentsService.studentChangePaymentCollectionDay.name +
            apiConstants._SUCCESS:
            if (!state.user || state.user.id !== action.data?.id) return state
            return {
                ...state,
                user: mergeFormDataUser(state.user, action.data),
            }
        case subscriptionService.updatePrioritizedSchoolSubjects.name +
            apiConstants._SUCCESS:
        case subscriptionService.updateAvailabilitySlots.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                user: action.data,
            }
        case subscriptionService.getSubscribePricing.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                user: action.data?.user,
                // L'email de la requête qui a rempli ce tiroir : voir
                // `useGetFastTrackUserOnMount`.
                pricingRequestEmail: action.urlParams?.email || null,
                objectiveNeeds: action.data?.user?.objective_needs,
                pricingTableData: action.data?.pricing_table,
                pricingTableIndividualData:
                    action.data?.pricing_table_individual,
                pricingTableV2: action.data?.pricing_table_v2,
                sessionsPerWeek: action.data?.sessions_per_week,
                selfServeNoCommitmentHours:
                    action.data?.self_serve_no_commitment_hours,
                schoolSubjects: action.data?.school_subjects,
                pricingTableDataNextMode: action.data?.pricing_table_next_mode,
                commitmentEndDate: action.data?.commitment_end_date,
                billingPortalUrl: action.data?.billing_portal_url,
                faq: action.data?.faq,
                holidayCourses: action.data?.holiday_courses,
                subscribedProductPriceId:
                    action.data?.subscribed_product_price_id,
                subscribedProductWithNoCommitment:
                    action.data?.subscribed_product_with_no_commitment,
                subscriptionMetadata: action.data?.subscription_metadata,
                invoiceShortUrl: action.data?.short_url,
                subscriptionStatus: action.data?.subscription_status,
                recommendedHoursPerWeek:
                    action.data?.recommended_hours_per_week,
                paymentVideoUrl: action.data?.payment_video_url,
            }
        case authService.getAvailableTrialLiveSessions.name +
            apiConstants._SUCCESS:
            return {
                ...state,
                trialOptions: action.data?.options,
            }

        case formDataConstants.SAVE_FORM_DATA:
            return {
                ...state,
                [action.formId]: {
                    ...state[action.formId],
                    [action.entryId]: action.data,
                },
            }

        case formDataConstants.RESET_FORM_DATA:
            return {
                ...state,
                [action.formId]: {
                    ...state[action.formId],
                    [action.entryId]: null,
                },
            }

        default:
            return state
    }
}
